import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { orderApi } from '../services/api'
import { useAuth } from '../hooks/useAuth'
import Button from '../components/common/Button'

/**
 * MyOrders — order history for the current user's homegroup.
 *
 * All users in a homegroup share the same cart AND order history,
 * since the cart is keyed by customer_id, not user_id. Anyone in
 * "Bubbas big fun house" sees the same list of orders.
 *
 * Filters:
 *   - Year (defaults to current year)
 *   - Month (defaults to all months)
 *
 * Click a row to see full detail. Each row also has a Cancel button
 * if the order is still pre-packed.
 */
const MONTHS = [
    { value: '', label: 'All months' },
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
]

export default function MyOrders() {
    const { user } = useAuth()
    const noCustomer = !user?.customerId
    const currentYear = new Date().getFullYear()

    const [orders, setOrders] = useState([])
    const [loading, setLoading] = useState(true)
    const [year, setYear] = useState('')      // empty = all years
    const [month, setMonth] = useState('')      // empty = all months
    const [cancellingId, setCancellingId] = useState(null)

    // Build year dropdown: current year and the last 4
    const years = [...Array(5)].map((_, i) => currentYear - i)

    useEffect(() => { load() }, [year, month])

    async function load() {
        setLoading(true)
        try {
            const response = await orderApi.mine(
                year || null,
                month || null
            )
            if (response.success) setOrders(response.orders)
            else toast.error(response.message || 'Could not load orders')
        } catch {
            toast.error('Network error')
        } finally {
            setLoading(false)
        }
    }

    async function handleCancel(order) {
        const reason = prompt(`Cancel order #${order.id}? (optional reason):`)
        if (reason === null) return  // user clicked Cancel on the prompt

        setCancellingId(order.id)
        try {
            const response = await orderApi.cancel(order.id, reason)
            if (response.success) {
                toast.success(`Order #${order.id} cancelled`)
                load()
            } else {
                toast.error(response.message || 'Cancel failed')
            }
        } catch {
            toast.error('Network error')
        } finally {
            setCancellingId(null)
        }
    }

    async function handlePreviewPdf(orderId) {
        try {
            const { blob } = await orderApi.fetchPdf(orderId, { inline: true })
            const url = URL.createObjectURL(blob)
            window.open(url, '_blank')
            setTimeout(() => URL.revokeObjectURL(url), 60_000)
        } catch {
            toast.error('Could not load PDF')
        }
    }

    async function handleDownloadPdf(orderId) {
        try {
            const { blob, fileName } = await orderApi.fetchPdf(orderId, { inline: false })
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = fileName
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            URL.revokeObjectURL(url)
        } catch {
            toast.error('Could not download PDF')
        }
    }

    // ----- No-customer state -------------------------------------------------
    if (noCustomer) {
        return (
            <div className="bg-white rounded-xl shadow-sm p-8 text-center max-w-xl mx-auto">
                <h2 className="text-xl font-semibold text-ghasc-navy">Account not linked yet</h2>
                <p className="text-gray-600 mt-2 text-sm">
                    Your account isn't linked to a homegroup yet, so you don't have order
                    history. Reach out to the literature chair to be linked.
                </p>
            </div>
        )
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                    <h2 className="text-2xl font-semibold text-ghasc-navy">My Orders</h2>
                    <p className="text-sm text-gray-500 mt-1">
                        Order history for <strong>{user?.customerName}</strong>.
                    </p>
                </div>
                <Link to="/shop"><Button variant="secondary">Place a new order</Button></Link>
            </div>

            {/* Filter bar */}
            <div className="bg-white rounded-xl shadow-sm p-4 flex flex-wrap items-end gap-3">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                    <select
                        value={year}
                        onChange={(e) => setYear(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                    >
                        <option value="">All years</option>
                        {years.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
                    <select
                        value={month}
                        onChange={(e) => setMonth(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                    >
                        {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                </div>
                {(year || month) && (
                    <button
                        onClick={() => { setYear(''); setMonth('') }}
                        className="text-sm text-ghasc-navy hover:underline pb-2"
                    >
                        Clear filters
                    </button>
                )}
            </div>

            {/* List */}
            {loading ? (
                <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-500">Loading...</div>
            ) : orders.length === 0 ? (
                <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-500">
                    No orders match those filters.
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {orders.map(o => {
                        const canCancel = o.status === 'pending_payment'
                            && o.submittedByUserId === user?.id
                        return (
                            <div key={o.id} className="bg-white rounded-xl shadow-sm p-4 flex flex-col gap-3">
                                {/* Top row: order id + status */}
                                <div className="flex items-start justify-between gap-2">
                                    <div>
                                        <Link to={`/orders/${o.id}`} className="text-ghasc-navy font-semibold hover:underline">
                                            Order #{o.id}
                                        </Link>
                                        <div className="text-xs text-gray-500 mt-0.5">
                                            {new Date(o.createdAt).toLocaleDateString('en-US', {
                                                month: 'short', day: 'numeric', year: 'numeric',
                                            })}
                                        </div>
                                    </div>
                                    <StatusBadge status={o.status} />
                                </div>

                                {/* Cycle + submitter */}
                                <div>
                                    <div className="text-sm font-medium text-gray-900">
                                        {cycleToDisplay(o.cycleLabel)} cycle
                                    </div>
                                    <div className="text-xs text-gray-500">
                                        Submitted by {o.submitterName}
                                    </div>
                                </div>

                                {/* Counts + total */}
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                    <div>
                                        <div className="text-xs uppercase tracking-wider text-gray-500">Items</div>
                                        <div className="font-medium">
                                            {o.lineCount}
                                            {o.backorderCount > 0 && (
                                                <span className="ml-1 text-xs text-amber-700">
                                                    ({o.backorderCount} BO)
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-xs uppercase tracking-wider text-gray-500">Total</div>
                                        <div className="font-semibold text-ghasc-navy">${Number(o.subtotal).toFixed(2)}</div>
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
                                    <button
                                        onClick={() => handlePreviewPdf(o.id)}
                                        className="flex-1 min-w-[100px] px-3 py-2 text-sm text-ghasc-navy bg-gray-50 rounded-lg hover:bg-gray-100"
                                    >
                                        Preview
                                    </button>
                                    <button
                                        onClick={() => handleDownloadPdf(o.id)}
                                        className="flex-1 min-w-[100px] px-3 py-2 text-sm text-ghasc-navy bg-gray-50 rounded-lg hover:bg-gray-100"
                                    >
                                        Download
                                    </button>
                                    {canCancel && (
                                        <button
                                            onClick={() => handleCancel(o)}
                                            disabled={cancellingId === o.id}
                                            className="flex-1 min-w-[100px] px-3 py-2 text-sm text-ghasc-danger bg-red-50 rounded-lg hover:bg-red-100 disabled:opacity-50"
                                        >
                                            {cancellingId === o.id ? 'Cancelling...' : 'Cancel'}
                                        </button>
                                    )}
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}

            <div className="text-xs text-gray-500">
                Showing {orders.length} order{orders.length === 1 ? '' : 's'}
            </div>
        </div>
    )
}

// ---------------------------------------------------------------------------
//  Status badge — colored pill with friendly label
// ---------------------------------------------------------------------------
function StatusBadge({ status }) {
    const map = {
        pending_payment: { label: 'Pending Payment', cls: 'bg-amber-100 text-amber-800' },
        paid: { label: 'Paid', cls: 'bg-blue-100 text-blue-800' },
        packed: { label: 'Packed', cls: 'bg-indigo-100 text-indigo-800' },
        picked_up: { label: 'Picked Up', cls: 'bg-green-100 text-green-800' },
        cancelled: { label: 'Cancelled', cls: 'bg-gray-100 text-gray-600' },
    }
    const m = map[status] || { label: status, cls: 'bg-gray-100 text-gray-700' }
    return (
        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${m.cls}`}>
            {m.label}
        </span>
    )
}

// ---------------------------------------------------------------------------
//  Cycle "2026-06" → "June 2026"
// ---------------------------------------------------------------------------
function cycleToDisplay(label) {
    if (!label || label.length !== 7) return label || ''
    const year = parseInt(label.slice(0, 4), 10)
    const month = parseInt(label.slice(5), 10)
    if (isNaN(year) || isNaN(month)) return label
    const months = ['January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December']
    return `${months[month - 1]} ${year}`
}